import type { LatLon } from '@sw/schema'
import { degToRad, radToDeg } from './vec.js'

/** WGS84 ellipsoid. */
const SEMI_MAJOR_AXIS_M = 6378137
const FIRST_ECCENTRICITY_SQUARED = 6.69437999014e-3

/** Offsets in metres from an origin, on a plane tangent to the ellipsoid. */
export interface EastNorth {
  east: number
  north: number
}

/**
 * Radii of curvature at a latitude. Using both the meridional and prime
 * vertical radii — rather than one spherical radius — keeps the tangent-plane
 * approximation accurate to centimetres over the few kilometres a sound world
 * spans, and costs nothing.
 */
function radiiAt(latDeg: number): { meridional: number; primeVertical: number } {
  const sinLat = Math.sin(degToRad(latDeg))
  const tmp = 1 - FIRST_ECCENTRICITY_SQUARED * sinLat * sinLat
  const primeVertical = SEMI_MAJOR_AXIS_M / Math.sqrt(tmp)
  const meridional = (SEMI_MAJOR_AXIS_M * (1 - FIRST_ECCENTRICITY_SQUARED)) / (tmp * Math.sqrt(tmp))
  return { meridional, primeVertical }
}

/** Metres east and north of `origin`. */
export function geoToEastNorth(origin: LatLon, point: LatLon): EastNorth {
  const { meridional, primeVertical } = radiiAt(origin.lat)
  const north = degToRad(point.lat - origin.lat) * meridional
  const east = degToRad(point.lon - origin.lon) * primeVertical * Math.cos(degToRad(origin.lat))
  return { east, north }
}

export function eastNorthToGeo(origin: LatLon, offset: EastNorth): LatLon {
  const { meridional, primeVertical } = radiiAt(origin.lat)
  const lat = origin.lat + radToDeg(offset.north / meridional)
  const lon =
    origin.lon + radToDeg(offset.east / (primeVertical * Math.cos(degToRad(origin.lat))))
  return { lat, lon }
}

/**
 * Great-circle distance, for comparing points too far apart for one tangent
 * plane — picking the nearest placement, say.
 *
 * The sphere is sized by the Gaussian mean radius at the midpoint latitude
 * rather than by the equatorial radius. Using the equatorial radius everywhere
 * is the common shortcut and it overstates distances by roughly two metres per
 * kilometre at temperate latitudes, which is enough to disagree visibly with
 * the tangent-plane maths used everywhere else in this package.
 */
export function greatCircleDistanceM(a: LatLon, b: LatLon): number {
  const lat1 = degToRad(a.lat)
  const lat2 = degToRad(b.lat)
  const dLat = lat2 - lat1
  const dLon = degToRad(b.lon - a.lon)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * gaussianRadiusAt((a.lat + b.lat) / 2) * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** sqrt(M·N) at a latitude, in closed form. */
function gaussianRadiusAt(latDeg: number): number {
  const sinLat = Math.sin(degToRad(latDeg))
  return (
    (SEMI_MAJOR_AXIS_M * Math.sqrt(1 - FIRST_ECCENTRICITY_SQUARED)) /
    (1 - FIRST_ECCENTRICITY_SQUARED * sinLat * sinLat)
  )
}
